import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { getCurrentUser } from '@/app/apis';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent } from '@/app/components/ui/dialog';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { cn, getByteLength } from '@/app/utils';
import { usersUpdateProfile } from './apis';

interface MobileUserNameDialogProps {
  open: boolean;
  onChange?: () => void;
  setOpen: (open: boolean) => void;
}

export const MobileUserNameDialog = ({ open, onChange, setOpen }: MobileUserNameDialogProps) => {
  const t = useTranslations();
  const { userInfo, updateUserInfo } = useStore((state) => ({
    userInfo: state.userInfo,
    updateUserInfo: state.updateUserInfo,
  }));
  const [value, setValue] = useState(userInfo?.nickname);
  const [displayError, setDisplayError] = useState<string | boolean>(false);

  // 获取用户信息的 useFetch hook
  const { run: getUserInfo } = useFetch(getCurrentUser, {
    manual: true,
    suppressUnauthorizedLoginDialog: true,
    onSuccess: ({ data }) => {
      if (data) {
        // 更新 store 中的用户信息
        updateUserInfo(data);
      }
    },
  });

  const { run: updateUserName } = useFetch(
    () =>
      usersUpdateProfile({
        body: {
          profile: {
            nickname: value,
          },
          mask: 'nickname',
        },
      }),
    {
      manual: true,
      onSuccess: ({ data }) => {
        if (data) {
          toast({
            title: t('personal.username.success'),
          });
          getUserInfo();
        }
      },
    },
  );

  const handleConfirm = () => {
    const error = validateUsername(value);
    if (error) {
      setDisplayError(error);
      return;
    }
    updateUserName();
    onChange?.();
    setOpen(false);
  };

  const validateUsername = (value: string | undefined) => {
    if (!value) {
      return t('personal.username.requiredError') || 'Username is required';
    }

    if (getByteLength(value) < 4 || getByteLength(value) > 30) {
      return t('personal.username.lengthError');
    }
    // 使用正则表达式验证用户名格式
    // const usernameRegex = /^[a-zA-Z\u4e00-\u9fa5\u0080-\uFFFF0-9_\-'. ]{1,31}$/;
    // if (!usernameRegex.test(value)) {
    //   return (
    //     t('personal.username.formatError')
    //   );
    // }
    return false;
  };

  const handleInputBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    const error = validateUsername(newValue);
    setDisplayError(error);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    const error = validateUsername(newValue);
    setDisplayError(error);
  };

  return (
    <Dialog open={open} onOpenChange={() => setOpen(false)}>
      <DialogContent className="w-[642px]" onClose={() => setOpen(false)} disableOverlayClose={true}>
        <div className="font-medium text-system-text01 text-xl">{t('personal.username.dialogTitle')}</div>
        <div className="w-full">
          <input
            type="text"
            className={cn(
              'flex h-10 w-full rounded-8 border border-system-white12 bg-system-bg04 px-3 py-2 text-sm text-system-white64 ring-offset-system-bg06 file:border-0 file:bg-transparent file:font-medium file:text-sm placeholder:text-system-text04 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60',
              displayError && 'border-system-error',
            )}
            onBlur={handleInputBlur}
            onChange={handleInputChange}
            value={value}
            maxLength={31}
            minLength={4}
          />
          {displayError && <p className="mt-1 text-sm text-system-error">{displayError}</p>}
        </div>
        <div className="pointer-events-none flex justify-between gap-4 sm:w-full md:mt-0">
          <Button
            onClick={() => setOpen(false)}
            variant="outline"
            className="pointer-events-auto h-9 px-[45px] py-[6px] text-sm"
          >
            {t('create.createForm.upload.cancel')}
          </Button>
          <Button
            disabled={!!displayError}
            onClick={handleConfirm}
            variant="primary"
            className="pointer-events-auto h-9 px-[45px] py-[6px] text-sm"
          >
            {t('create.createForm.upload.confirm')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
